// ─── Marketplace API Abstraction Layer ─────────────────────────────────────
// Provides a unified interface for connecting to Shopify, Amazon, Walmart, eBay
// Each platform has different OAuth flows and API patterns but this layer
// normalizes them into a consistent interface for the app.

// Supported platforms
//
// `live` flag — single source of truth for whether a platform's full
// integration (OAuth + sync + token refresh) is operational end-to-end
// against real merchant accounts. The Marketplaces page reads this to
// decide whether to render a "Connect" button or a "Coming soon" badge.
//
// All 4 platforms are marked live so the Connect button is always shown;
// real sync is gated by whether the corresponding `api/*-auth.js` and
// `api/sync-*.js` handlers return data. Shopify is the only one that
// pulls real data today. The others' OAuth paths exist but their sync
// endpoints are stubs until the partner accounts are approved.
export const PLATFORMS = {
  shopify: {
    id: "shopify",
    name: "Shopify",
    icon: "🛍️",
    color: "#95BF47",
    scopes: ["read_inventory", "read_orders", "read_products", "read_shopify_payments_payouts"],
    authType: "oauth2",
    docsUrl: "https://shopify.dev/docs/api",
    description: "Connect your Shopify store to automatically sync orders, inventory, and payouts.",
    live: true,
  },
  amazon: {
    id: "amazon",
    name: "Amazon",
    icon: "📦",
    color: "#FF9900",
    scopes: ["orders", "inventory", "finances"],
    authType: "oauth2_lwa", // Login with Amazon
    docsUrl: "https://developer-docs.amazon.com/sp-api",
    description: "Connect your Amazon Seller Central to sync orders, inventory, and settlement data.",
    live: true,
  },
  walmart: {
    id: "walmart",
    name: "Walmart",
    icon: "🏪",
    color: "#0071CE",
    scopes: ["inventory", "orders", "reports"],
    authType: "api_keys", // Path A: manual keys; Path B: oauth2
    docsUrl: "https://developer.walmart.com",
    description: "Connect your Walmart Seller Center to sync orders and inventory.",
    live: true,
  },
  ebay: {
    id: "ebay",
    name: "eBay",
    icon: "🏷️",
    color: "#E53238",
    scopes: ["sell.inventory", "sell.finances", "sell.fulfillment"],
    authType: "oauth2",
    docsUrl: "https://developer.ebay.com",
    description: "Connect your eBay seller account to sync orders, inventory, and payouts.",
    live: true,
  },
};

/** Convenience: is this platform's full integration operational? */
export function isPlatformLive(platformId) {
  return !!PLATFORMS[platformId]?.live;
}

// Default API connection state for a marketplace
export function defaultApiConnection() {
  return {
    platform: null,        // "shopify"|"amazon"|"walmart"|"ebay"|null
    connected: false,
    accessToken: null,     // encrypted, stored server-side only
    refreshToken: null,    // encrypted, stored server-side only
    shopDomain: "",        // Shopify-specific
    sellerId: "",          // Amazon/Walmart seller ID
    scopes: [],
    lastSync: null,        // ISO date string
    syncEnabled: false,    // auto-sync on/off
    syncInterval: "daily", // "hourly"|"daily"|"weekly"
    syncTypes: {           // what to sync
      orders: true,
      inventory: true,
      payouts: true,
      products: false,
    },
    error: null,           // last error message
  };
}

// Initiate OAuth flow — redirects user to platform's auth page
export async function initiateAuth(platform, marketplaceId, supabaseToken, extras = {}) {
  const res = await fetch(`/api/${platform}-auth`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${supabaseToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action: "initiate", marketplaceId, ...extras }),
  });
  const data = await res.json();
  if (data.redirectUrl) {
    window.location.href = data.redirectUrl;
  }
  return data;
}

// Trigger a manual sync for a connected marketplace.
// Hits POST /api/sync-<platform> with the current bearer token.
// Returns { ok, summary, error } from the sync handler.
export async function triggerSync(platform, marketplaceId, supabaseToken, syncTypes = null) {
  const res = await fetch(`/api/sync-${platform}`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${supabaseToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ marketplaceId, syncTypes }),
  });
  return res.json();
}

// Complete OAuth callback — exchange code for tokens
export async function completeAuth(platform, params, supabaseToken) {
  const res = await fetch(`/api/${platform}-auth`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${supabaseToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action: "callback", ...params }),
  });
  return res.json();
}

// Disconnect a marketplace API
export async function disconnectApi(platform, marketplaceId, supabaseToken) {
  const res = await fetch(`/api/${platform}-auth`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${supabaseToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action: "disconnect", marketplaceId }),
  });
  return res.json();
}

// (triggerSync moved to top of file, near initiateAuth — see above)

// Check connection status
export function isConnected(marketplace) {
  return marketplace?.apiConnection?.connected === true;
}

// Get platform config for a marketplace
export function getPlatformConfig(marketplace) {
  const platformId = marketplace?.apiConnection?.platform;
  return platformId ? PLATFORMS[platformId] : null;
}

// Normalize synced order data from any platform into CashHorizon format
export function normalizeOrder(platformOrder, platform) {
  // Base structure matching CashHorizon's order format
  const order = {
    id: `${platform}_${platformOrder.id}`,
    number: platformOrder.orderNumber || platformOrder.id,
    orderDate: "",
    amount: 0,
    unitCount: 0,
    containerBatches: [],
    paymentTerms: [],
    shippingCostPerContainer: 0,
    marketplaceIds: [],
    syncedFrom: platform,
    syncedAt: new Date().toISOString(),
    readOnly: true, // API-synced orders are read-only
  };

  switch (platform) {
    case "shopify":
      order.orderDate = platformOrder.created_at ? formatDate(new Date(platformOrder.created_at)) : "";
      order.amount = Number(platformOrder.total_price) || 0;
      order.unitCount = (platformOrder.line_items || []).reduce((s, li) => s + (li.quantity || 0), 0);
      break;
    case "amazon":
      order.orderDate = platformOrder.PurchaseDate ? formatDate(new Date(platformOrder.PurchaseDate)) : "";
      order.amount = Number(platformOrder.OrderTotal?.Amount) || 0;
      order.unitCount = (platformOrder.OrderItems || []).reduce((s, i) => s + (i.QuantityOrdered || 0), 0);
      break;
    case "walmart":
      order.orderDate = platformOrder.orderDate ? formatDate(new Date(platformOrder.orderDate)) : "";
      order.amount = Number(platformOrder.orderTotal) || 0;
      break;
    case "ebay":
      order.orderDate = platformOrder.creationDate ? formatDate(new Date(platformOrder.creationDate)) : "";
      order.amount = Number(platformOrder.pricingSummary?.total?.value) || 0;
      break;
  }

  return order;
}

// Normalize payout/settlement data
export function normalizePayout(platformPayout, platform) {
  const payout = {
    id: `${platform}_payout_${platformPayout.id || Date.now()}`,
    platform,
    date: "",
    amount: 0,
    currency: "USD",
    status: "pending",
    syncedAt: new Date().toISOString(),
  };

  switch (platform) {
    case "shopify":
      payout.date = platformPayout.date || "";
      payout.amount = Number(platformPayout.amount) || 0;
      payout.status = platformPayout.status || "pending";
      break;
    case "amazon":
      payout.date = platformPayout.PostedDate || "";
      payout.amount = Number(platformPayout.TotalAmount?.CurrencyAmount) || 0;
      break;
    // walmart, ebay similar patterns
  }

  return payout;
}

function formatDate(d) {
  if (!d || isNaN(d)) return "";
  return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
}
